import { motion } from "framer-motion";
import {
  Bike,
  CircleDot,
  Flame,
  Gamepad2,
  Home,
  Mountain,
  Palmtree,
  PawPrint,
  Sailboat,
  Star,
  Trees,
  Trophy,
  Waves,
} from "lucide-react";
import { AMENITIES, ASSETS } from "../../data/content";
import { FadeUp, MaskLineInView } from "./Reveal";

const ICONS = {
  Trophy,
  Gamepad2,
  CircleDot,
  Palmtree,
  Home,
  Trees,
  Waves,
  Sailboat,
  Mountain,
  Bike,
  Flame,
  Star,
  PawPrint,
};

export const Amenities = () => {
  return (
    <section
      id="amenities"
      data-testid="amenities-section"
      className="bg-[#fbf8f0] py-12 lg:py-16"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-3xl">
          <FadeUp>
            <span className="font-body text-[#c56928] text-[13px] tracking-[0.3em] uppercase">
              Amenities &amp; Experiences
            </span>
          </FadeUp>
          <h2 className="font-display text-[#07412b] text-[52px] sm:text-7xl lg:text-[110px] leading-[1] mt-2">
            <MaskLineInView>Fill your days.</MaskLineInView>
            <MaskLineInView delay={0.08}>Or don't. Both are welcome.</MaskLineInView>
          </h2>
          <FadeUp delay={0.15}>
            <p className="font-body font-light text-[#07412b]/75 text-base sm:text-lg mt-5 max-w-xl leading-relaxed">
              A house made for slowing down — and everything you'd want when you
              feel like doing more.
            </p>
          </FadeUp>
        </div>

        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {AMENITIES.map((a, i) => {
            const Icon = ICONS[a.icon] ?? Star;
            return (
              <motion.div
                key={a.title}
                data-testid={`amenity-${i + 1}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-8%" }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: (i % 4) * 0.05 }}
                className="group relative flex items-start gap-4 rounded-2xl border border-[#07412b]/10 bg-white/70 p-4 sm:p-5 hover:border-[#ddb128] hover:bg-white transition-colors duration-400"
              >
                <span className="h-11 w-11 shrink-0 rounded-full bg-[#07412b] text-[#ddb128] flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
                  <Icon size={19} strokeWidth={1.7} />
                </span>
                <div className="min-w-0">
                  <p className="font-display text-[#07412b] text-[17px] sm:text-lg leading-tight">
                    {a.title}
                  </p>
                  {a.note && (
                    <p className="font-body text-[#c56928] text-[11px] tracking-[0.14em] uppercase mt-1">
                      {a.note}
                    </p>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Pet Friendly badge */}
        <motion.div
          data-testid="pet-friendly-badge"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-8 sm:mt-10 overflow-hidden rounded-3xl bg-[#07412b] text-[#f7f3e9] shadow-[0_30px_60px_-30px_rgba(7,65,43,0.5)]"
        >
          <div className="flex flex-col sm:flex-row items-stretch">
            <div className="relative sm:w-[45%] lg:w-[38%] h-56 sm:h-auto shrink-0">
              <img
                src={ASSETS.gPetDog}
                alt="A golden retriever guest, at home on the lawn"
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover object-[center_35%]"
              />
              <div className="absolute inset-0 sm:bg-gradient-to-r sm:from-transparent sm:to-[#07412b]/60" />
              <div className="absolute inset-0 sm:hidden bg-gradient-to-t from-[#07412b] via-transparent to-transparent" />
            </div>

            <div className="relative flex-1 p-6 sm:p-8 lg:p-10 flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 self-start">
                <PawPrint size={16} className="text-[#ddb128]" />
              </div>
              <h3 className="font-display text-[#f7f3e9] text-4xl sm:text-5xl lg:text-6xl leading-[1] mt-3">
                Pet Friendly
              </h3>
              <p className="font-script text-[#ddb128] text-3xl sm:text-4xl mt-3 leading-none">
                Your good boys &amp; girls are welcome too.
              </p>
              <p className="font-body font-light text-[#f7f3e9]/75 text-sm sm:text-base mt-4 max-w-md leading-relaxed">
                An open lawn, a running river and a house full of humans who'd
                rather have your dog than not. Just let us know when you book.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
